/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2026 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { t } from 'i18next';
import { css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { getShowProductDocuments, ProductDocumentDto } from '@/api-sdk';
import { baseStyles } from '@/styles';
import { i18nInit, setBaseUrl } from '@/utils';

@customElement('fg-product-documents')
export class FgProductDocuments extends LitElement {
  @property({ type: String })
  country: string | undefined = undefined;

  @property({ type: String })
  locale = '';

  @property({ type: String })
  product = '';

  @property({ type: String })
  baseUrl = '';

  @state()
  isLoading: boolean = true;

  @state()
  documents: ProductDocumentDto[] = [];

  async scheduleUpdate() {
    setBaseUrl(this.baseUrl);

    window.i18next = window.i18next || i18nInit(this.locale);
    await window.i18next;

    super.scheduleUpdate();
  }

  async firstUpdated() {
    try {
      this.documents = await getShowProductDocuments({
        productCode: this.product,
        params: {
          country: this.country,
          locale: this.locale,
        },
      });
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  }

  renderDocuments(documents: ProductDocumentDto[] | null) {
    if (documents && documents.length > 0) {
      return documents.map(
        document => html`
          <li class="document-item">
            <a class="document-link" href=${document.url} target="_blank">
              ${document.name}
            </a>
          </li>
        `,
      );
    }

    return html`
      <span class="no-downloads-text">${t('labels.noDownloadsAvailable')}</span>
    `;
  }

  render() {
    if (this.isLoading) {
      return html`
        <div class="loading-container">
          <fg-loader .type=${'in-component'}></fg-loader>
        </div>
      `;
    }
    return html`
      <ul>
        ${this.renderDocuments(this.documents)}
      </ul>
    `;
  }

  static styles = [
    baseStyles,
    css`
      .loading-container {
        position: relative;
        height: 200px;
      }

      .no-downloads-text,
      .document-link {
        color: var(--fg-gray--800, var(--fg-fallback-gray--800));
        text-decoration-thickness: 0.1rem;
      }

      .document-link {
        font-weight: 400;
      }

      .document-item:last-of-type {
        margin-bottom: 0px;
      }

      .document-item {
        margin-bottom: 0.5rem;
        list-style: inside;

        &&::marker {
          color: var(--fg-gray--400, var(--fg-fallback-gray--400));
        }
      }
    `,
  ];
}
